import Image from '@base/FutureImage';
import BatchCard from 'aquilla/core/organisms/BatchCard';
import { getLink as getBatchLink } from '@models/Batch';
import * as React from 'react';
import { emptyList } from '@constants/empty';
import { getDay, getMonthString, getYear } from '@utils/dates';
import { getBatchTag } from '@comp/BatchCard/util';
import { batchTypes } from '@constants/index';

interface IAuthor {
  name?: string;
  firstName?: string;
  lastName?: string;
}

interface NewBatchCardProps {
  batch: {
    thumbnail: string;
    coverPhoto: string;
    name: string;
    startsAt: string;
    endAt: string;
    languages: {
      label: string;
    }[];
    timeType: {
      text: string;
    };
    authors: IAuthor[];
    isPrimaryCentreBatch: boolean;
    batchType: number;
  };
  className?: string;
  as?: React.ElementType<any>;
  actionButtons?: Array<React.ReactNode>;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

export default function NewBatchCard(props: NewBatchCardProps) {
  const { batch, className, actionButtons, as, onClick } = props;
  const thumbnail = () => (
    <Image
      alt="Thumbnail"
      src={batch.thumbnail || batch.coverPhoto}
      width={272}
      height={156}
    />
  );

  return (
    <BatchCard
      as={as}
      className={className}
      href={getBatchLink(batch)}
      thumbnail={thumbnail}
      title={batch.name}
      langLabel={batch.languages?.[0]?.label}
      topicName={getBatchTag(batch)}
      topicColor="#57B4F8"
      startsIn={batchDateInfoText(batch.startsAt, batch.endAt)}
      batchTiming={batch.timeType?.text}
      educatorNames={batchAuthorText(batch.authors)}
      isCentreBatch={
        batch.batchType === batchTypes.CENTRE_BATCH &&
        batch.isPrimaryCentreBatch
      }
      actionButtons={actionButtons?.length > 0 ? actionButtons : null}
      onClick={onClick}
    />
  );
}

NewBatchCard.defaultProps = {
  className: '',
  as: 'a',
  actionButtons: []
};

function batchDateInfoText(startAt: string, endAt: string): string {
  if (!startAt) return '';

  const start = new Date(startAt).setHours(0, 0, 0, 0);
  const end = endAt && new Date(endAt).setHours(0, 0, 0, 0);
  const current = new Date().setHours(0, 0, 0, 0);
  const startText = `${getDay(startAt)} ${getMonthString(startAt)} ${getYear(
    startAt
  )}`;
  const endText =
    endAt && `${getDay(endAt)} ${getMonthString(endAt)} ${getYear(endAt)}`;

  // For already stated batches
  if (start < current && (!end || current < end)) {
    if (!end || current < end) return `Started on ${startText}`;
    return `Ended on ${endText}`;
  }

  const oneDay = 24 * 60 * 60 * 1000;
  const diff = Math.ceil((start - current) / oneDay);

  if (diff < 1) return `Starts today · ${startText}`;
  if (diff < 2) return `Starts tomorrow · ${startText}`;
  if (diff > 1 && diff < 8) return `Starts in ${diff} days · ${startText}`;
  return `Starts on ${startText}`;
}

function batchAuthorText(authors: IAuthor[]): string {
  const authorsNameArr =
    authors?.map(
      (author) => author.name || `${author.firstName} ${author.lastName}`
    ) || emptyList;

  if (authorsNameArr.length === 0) return '';
  if (authorsNameArr.length === 1) return authorsNameArr[0];

  if (authorsNameArr.length > 1 && authorsNameArr.length < 4) {
    return `${authorsNameArr
      .slice(0, -1)
      .join(', ')} and ${authorsNameArr.slice(-1)}`;
  }
  return `${authorsNameArr.slice(0, 3).join(', ')} and ${
    authorsNameArr.length - 3
  } more`;
}
